import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDisplayScale } from '../lib/useDisplayScale';
import DisplayScaleControl from './DisplayScaleControl';
import { setUiPreferences, useUiPreferences } from '../lib/useUiPreferences';

export default function ViewControl({ guideActive = false }: { guideActive?: boolean }) {
  const ui = useUiPreferences();
  const displayScale = useDisplayScale();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      if (!button.current || !popup.current) return;
      const scale = Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      const anchor = button.current.getBoundingClientRect();
      const width = window.innerWidth / scale;
      const top = anchor.bottom / scale + 6;
      popup.current.style.left = `${Math.max(8, Math.min(anchor.left / scale, width - 344))}px`;
      popup.current.style.top = `${top}px`;
      popup.current.style.maxHeight = `${Math.max(80, window.innerHeight / scale - top - 8)}px`;
    };
    // Header status rows can appear asynchronously without resizing the button.
    // Follow its actual position only while the menu is open.
    let frame = 0;
    const follow = () => { position(); frame = requestAnimationFrame(follow); };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [open, displayScale]);
  useEffect(() => {
    if (!open) return;
    popup.current?.querySelector<HTMLElement>('button')?.focus();
    const close = (event: PointerEvent) => {
      if (!popup.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape); };
  }, [open]);
  if (window.self !== window.top) return null;
  return <>
    <button ref={button} className="cui-view-button" data-ai-id="cui-view-menu" aria-expanded={open} aria-haspopup="dialog"
      onClick={() => setOpen(!open)}>Ansicht · {ui.mode === 'auto' ? 'Auto' : ui.mode === 'compact' ? 'Kompakt' : 'Standard'}</button>
    {open && createPortal(<div ref={popup} className="cui-view-popup" role="dialog" aria-label="Ansicht einstellen">
      <div className="cui-view-heading">Darstellung <button aria-label="Ansicht schließen" onClick={() => { setOpen(false); button.current?.focus(); }}>×</button></div>
      <div className="cui-view-options">{(['auto', 'standard', 'compact'] as const).map(mode =>
        <button key={mode} aria-pressed={ui.mode === mode} data-ai-id={`cui-view-${mode}`} onClick={() => setUiPreferences({ mode })}>
          {{ auto: 'Auto', standard: 'Standard', compact: 'Kompakt' }[mode]}</button>)}</div>
      <p>Auto passt Abstände und Nebendetails an den verfügbaren Platz an.</p>
      {guideActive && <>
      <label><input type="checkbox" checked={ui.focus} onChange={e => setUiPreferences({ focus: e.target.checked })} /> Fokus auf Bühne (mit Guide und Kanzler)</label>
      <label><input type="checkbox" checked={!ui.focus && ui.guideCollapsed === true} onChange={e => setUiPreferences({ guideCollapsed: e.target.checked, focus: false })} /> Guide einklappen</label>
      <label><input type="checkbox" checked={ui.coachHidden} onChange={e => setUiPreferences({ coachCollapsed: e.target.checked, focus: false })} /> Kanzler einklappen</label>
      </>}
      <div className="cui-view-heading">Größe der Oberfläche</div><DisplayScaleControl /><p>Kleinere Prozentwerte schaffen mehr Platz. Die Auswahl wird auf diesem Gerät gespeichert.</p>
      <button onClick={() => setUiPreferences({ mode: 'auto', guideCollapsed: null, coachCollapsed: null, focus: false })}>Automatische Aufteilung zurücksetzen</button>
    </div>, document.body)}
  </>;
}
